import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { callFunction } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { isWherebyUiEnabled, pickVideoService, SPONTANEOUS_VIDEO_MINUTES } from '@/lib/whereby';
import { appointmentDetailQueryKey } from '@/hooks/useAppointments';
import { patientAppointmentsQueryKey } from '@/hooks/usePatientAppointments';

export interface WherebyStatus {
  enabled: boolean;
  configured: boolean;
  hasClinicApiKey: boolean;
}

export interface WherebyMeetingResult {
  appointmentId: string;
  participantUrl: string | null;
  hostUrl: string | null;
  meetingId: string | null;
}

export const wherebyStatusQueryKey = (clinicId: string | null | undefined) =>
  ['whereby', 'status', clinicId] as const;

export const wherebyEnabledQueryKey = (clinicId: string | null | undefined) =>
  ['whereby', 'enabled', clinicId] as const;

export function useWherebyEnabled(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: wherebyEnabledQueryKey(clinicId),
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    queryFn: async (): Promise<boolean> => {
      if (!clinicId) return false;
      const { data, error } = await supabase
        .from('clinics')
        .select('whereby_enabled')
        .eq('id', clinicId)
        .single();
      if (error || !data) return false;
      return isWherebyUiEnabled(
        Boolean((data as { whereby_enabled?: boolean }).whereby_enabled),
      );
    },
  });
}

export function useWherebyStatus(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: wherebyStatusQueryKey(clinicId),
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    queryFn: async (): Promise<WherebyStatus> => {
      if (!clinicId) {
        return { enabled: false, configured: false, hasClinicApiKey: false };
      }
      return await callFunction<{ clinicId: string }, WherebyStatus>('whereby-status', {
        clinicId,
      });
    },
  });
}

export function useUpdateWherebySettings(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      enabled: boolean;
      apiKey?: string;
      clearApiKey?: boolean;
    }): Promise<WherebyStatus> => {
      if (!clinicId) throw new Error('clinicId is required');
      return await callFunction<
        { clinicId: string; enabled: boolean; apiKey?: string; clearApiKey?: boolean },
        WherebyStatus
      >('whereby-update-settings', {
        clinicId,
        enabled: input.enabled,
        apiKey: input.apiKey,
        clearApiKey: input.clearApiKey,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: wherebyStatusQueryKey(clinicId) });
      qc.invalidateQueries({ queryKey: wherebyEnabledQueryKey(clinicId) });
      qc.invalidateQueries({ queryKey: ['clinics', clinicId] });
    },
  });
}

export function useProvisionWherebyMeeting(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (appointmentId: string): Promise<WherebyMeetingResult> => {
      if (!clinicId) throw new Error('clinicId is required');
      return await callFunction<
        { action: 'provision'; clinicId: string; appointmentId: string },
        WherebyMeetingResult
      >('whereby-meetings', { action: 'provision', clinicId, appointmentId });
    },
    onSuccess: (result) => {
      invalidateVideoQueries(qc, clinicId, result.appointmentId);
    },
  });
}

export function useSyncWherebyMeeting(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointmentId: string;
      reason: 'rescheduled' | 'cancelled';
    }): Promise<WherebyMeetingResult> => {
      if (!clinicId) throw new Error('clinicId is required');
      return await callFunction<
        {
          action: 'sync';
          clinicId: string;
          appointmentId: string;
          reason: 'rescheduled' | 'cancelled';
        },
        WherebyMeetingResult
      >('whereby-meetings', {
        action: 'sync',
        clinicId,
        appointmentId: input.appointmentId,
        reason: input.reason,
      });
    },
    onSuccess: (result) => {
      invalidateVideoQueries(qc, clinicId, result.appointmentId);
    },
  });
}

export function useJoinWherebyMeeting(clinicId: string | null | undefined) {
  return useMutation({
    mutationFn: async (appointmentId: string): Promise<WherebyMeetingResult> => {
      if (!clinicId) throw new Error('clinicId is required');
      return await callFunction<
        { action: 'join'; clinicId: string; appointmentId: string },
        WherebyMeetingResult
      >('whereby-meetings', { action: 'join', clinicId, appointmentId });
    },
  });
}

export function useStartWherebyMeeting(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      patientId: string;
      clinicianId?: string | null;
      services?: ReadonlyArray<{ id: string; name: string; duration_minutes?: number | null }>;
    }): Promise<WherebyMeetingResult> => {
      if (!clinicId) throw new Error('clinicId is required');
      const videoService = input.services ? pickVideoService(input.services) : null;
      return await callFunction<
        {
          action: 'start';
          clinicId: string;
          patientId: string;
          clinicianId?: string;
          serviceId?: string;
          durationMinutes?: number;
        },
        WherebyMeetingResult
      >('whereby-meetings', {
        action: 'start',
        clinicId,
        patientId: input.patientId,
        clinicianId: input.clinicianId ?? undefined,
        serviceId: videoService?.id,
        durationMinutes: videoService?.duration_minutes ?? SPONTANEOUS_VIDEO_MINUTES,
      });
    },
    onSuccess: (result) => {
      invalidateVideoQueries(qc, clinicId, result.appointmentId);
    },
  });
}

export function useAppointmentVideoHost(
  clinicId: string | null | undefined,
  appointmentId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['whereby', 'host', clinicId, appointmentId] as const,
    enabled: Boolean(clinicId && appointmentId),
    queryFn: async (): Promise<string | null> => {
      if (!clinicId || !appointmentId) return null;
      const { data, error } = await supabase
        .from('appointment_video_meetings')
        .select('host_url')
        .eq('clinic_id', clinicId)
        .eq('appointment_id', appointmentId)
        .maybeSingle();
      if (error || !data) return null;
      return ((data as { host_url?: string | null }).host_url ?? null);
    },
  });
}

function invalidateVideoQueries(
  qc: ReturnType<typeof useQueryClient>,
  clinicId: string | null | undefined,
  appointmentId: string,
) {
  qc.invalidateQueries({ queryKey: ['appointments'] });
  qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(appointmentId) });
  qc.invalidateQueries({ queryKey: patientAppointmentsQueryKey(null) });
  qc.invalidateQueries({ queryKey: ['whereby', 'host', clinicId, appointmentId] });
}
